import {type ColorChangeSource} from '../../components/ColorPicker';
import {type RunSelectorAnalyticsLocation} from '../../components/RunSelector/RunSelectorAnalyticsLocationContext';
import {type RunColorSource} from '../../util/colors';
import {makeTrackEvent} from './makeTrackEvent';

export type RunColorAction =
  | 'Open color picker'
  | 'Set color'
  | 'Reset color'
  | 'Workspace with run group coloring loaded';

export type RunColorMethod = 'hex_or_swatch' | 'gradient' | 'field';

// react-color uses 'hex' for both preset swatches and hex-field entries, so the
// event deliberately preserves that ambiguity instead of mislabeling field input.
export function getRunColorMethod(source?: ColorChangeSource): RunColorMethod {
  switch (source) {
    case 'hex':
      return 'hex_or_swatch';
    case 'rgb':
      return 'field';
    default:
      return 'gradient';
  }
}

type RunColorInteractionEvent = {
  action: Exclude<RunColorAction, 'Workspace with run group coloring loaded'>;
  affectedRunCount: number;
  customRunColorSource: RunColorSource;
  hasCustomRunColorDescendantColors: boolean;
  hasParentInheritedColor: boolean;
  isGroupingEnabled: boolean;
  isParentGroup: boolean;
  location: RunSelectorAnalyticsLocation;
  method?: RunColorMethod;
  rowDepth: number;
};

type RunGroupColorWorkspaceLoadEvent = {
  action: 'Workspace with run group coloring loaded';
  isGroupingEnabled: true;
  location: RunSelectorAnalyticsLocation;
};

export type RunColorEvent =
  | RunColorInteractionEvent
  | RunGroupColorWorkspaceLoadEvent;

type AnnotatedRunColorEvent = Pick<
  RunColorInteractionEvent,
  'isGroupingEnabled' | 'location'
> &
  Partial<
    Omit<RunColorInteractionEvent, 'action' | 'isGroupingEnabled' | 'location'>
  > & {
    action: RunColorAction;
  };

const trackRunColorEvent = makeTrackEvent<
  AnnotatedRunColorEvent,
  {
    action: {
      description: 'the color action or workspace-load state. the three reset button labels map onto Reset color: hasParentInheritedColor gives "Use inherited color", isParentGroup gives "Reset group colors", neither gives "Reset color"';
      exampleValues: [
        'Open color picker',
        'Set color',
        'Reset color',
        'Workspace with run group coloring loaded',
      ];
    };
    affectedRunCount: {
      description: 'how many runs the color applies to: every run under a group row, or 1 for a single run row';
    };
    customRunColorSource: {
      description: 'where the row color came from before the action';
      exampleValues: ['automatic', 'explicit', 'inherited'];
    };
    hasCustomRunColorDescendantColors: {
      description: 'whether any run under this group already had an explicit color';
    };
    hasParentInheritedColor: {
      description: 'whether the row was inheriting a color from a parent group. on Reset color this separates reverting to the inherited color from clearing the color outright';
    };
    isGroupingEnabled: {
      description: 'whether the runs table is grouped, i.e. whether a color can cascade to children';
    };
    isParentGroup: {
      description: 'whether the row is a group containing other groups, the parent-group coloring entry point';
    };
    location: {
      description: 'where the action was triggered from';
    };
    method: {
      description: 'how the color was picked, only set on Set color. react-color does not distinguish a preset swatch click from hex-field input, so both use hex_or_swatch';
      exampleValues: ['hex_or_swatch', 'gradient', 'field'];
    };
    rowDepth: {
      description: 'nesting depth of the row in the runs table, 0 for top level';
    };
    _description: 'Tracks run and group color changes in the run selector';
    _location: 'report, runs-table, workspace, create-sweep-prior-runs, sweep-prior-runs, full-screen-panel-view';
    _motivation: 'measure adoption of parent group color cascading and group color reset';
  }
>('Run selector run color');

export const trackRunColor = (event: RunColorEvent) =>
  trackRunColorEvent(event);
